import { evolve, pipe } from 'remeda'
import { z } from 'zod'
import { checkForInaccurateSetId } from '~/features/models/models-list/validators/common'
import { appendWarning } from '~/features/models/models-list/validators/utils'
import { Stores, InventoryStatus, InventoryV2 } from '~/supabase/types'

const locations = ['mandarin', 'wollem'] as const satisfies Stores[]

const inventoryStatuses = ['available', 'sold', 'unavailable'] as const satisfies InventoryStatus[]

const inventorySchema: z.ZodType<
  Omit<
    InventoryV2,
    | 'id'
    | 'created_at'
    | 'stock_code'
    | 'import_file_name'
    | 'sales'
    | 'inventory_item_id'
    | 'product_variant_id'
  > & {
    warning?: string[]
    quantity: number
    import_file_name?: string | null
    sales?: number | null
    inventory_item_id?: string | null
    product_variant_id?: string | null
  }
> = z
  .object({
    stock_code_v2: z.string(),
    location: z.enum(locations),
    // we need this to duplicate values so we have 1 quantity for each entry
    quantity: z.number().min(1),
    status: z.enum(inventoryStatuses),
    warning: z.array(z.string()).default([] as string[]),
    cost: z.number(),
    size: z.number(),
    import_file_name: z.string().nullable().optional().default(null),
    sales: z.number().nullable().optional().default(null),
    inventory_item_id: z.string().nullable().optional().default(null),
    product_variant_id: z.string().nullable().optional().default(null),
  })
  .superRefine((data, ctx) => {
    const { warning } = pipe(data, checkForZeroQuantities)

    if (warning && warning.length > 0) {
      warning.forEach((message) => {
        ctx.addIssue({ code: 'custom', message })
      })
    }

    const message = checkForInaccurateSetId(data.stock_code_v2)
    if (message) ctx.addIssue({ code: 'custom', path: ['stock_code'], message })
  })

export const inventoryArraySchema = z.array(inventorySchema)

export const inventorySample: Omit<InventorySchemaT, 'warning' | 'import_file_name' | 'sales'>[] = [
  {
    stock_code_v2: 'GORI00100',
    location: 'mandarin',
    status: 'available',
    quantity: 1,
    cost: 1000,
    size: 52,
    inventory_item_id: null,
    product_variant_id: null,
  },
]

export type InventorySchemaT = z.TypeOf<typeof inventorySchema>
export type InventoryArraySchemaT = z.TypeOf<typeof inventoryArraySchema>

export const checkForDuplicates = (value: InventoryArraySchemaT) => {
  return value.reduce<{ warning: string[] }>(
    (acc, v, index, array) => {
      const firstIndex = array.findIndex(
        (item) => item.stock_code_v2 === v.stock_code_v2 && item.size === v.size
      )
      if (firstIndex !== index)
        return evolve(acc, {
          warning: appendWarning(`Duplicate found - code: ${v.stock_code_v2} , size: ${v.size}`),
        })

      return acc
    },
    { warning: [] }
  )
}

export const checkForZeroQuantities = (v: InventorySchemaT) =>
  v.quantity === 0 ? evolve(v, { warning: appendWarning('Has zero quantity') }) : v
